import {
  Box,
  Typography,
  Link,
  Avatar,
  Container,
  Rating,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  LocationOnOutlined,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
  Web,
} from '@mui/icons-material';
import { Btn } from '../../components/index';
import { theme, translationMap, mapBackendKeyToStateKey } from '../../lib/index';
import AudioPlayerDetail from './AudioPlayerDetail';
import Focus from './Focus';

function About({ aboutData }) {
  return (
    <Wrapper>
      {aboutData !== {} ? (
        <>
          <PersonalDataBox>
            {aboutData.image ? (
              <Image
                variant="square"
                alt={aboutData.image.alt}
                src={aboutData.image.src}
              />
            ) : null}
            <TextBox>
              <Typography variant="h3">{aboutData.name}</Typography>
              <LocationBox>
                <LocationOnOutlined />
                <Typography>{aboutData.location}</Typography>
              </LocationBox>
              <Stats>
                {aboutData.rating ? (
                  <>
                    <Rating
                      name="rating"
                      value={parseFloat(aboutData.rating)}
                      precision={0.1}
                      readOnly
                    />
                    <Typography>
                      (
                      {aboutData.ratingCount}
                      )
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ flexGrow: 1 }} />
                )}
              </Stats>
              {aboutData.languages.length !== 0 && (
              <>
                <Pbold variant="p" sx={{ display: 'block' }}>
                  Sprachen:
                  {' '}
                </Pbold>
                <Typography
                  variant="p"
                  sx={{ display: 'block', marginBottom: '20px' }}
                >
                  {aboutData.languages
                    .map((e) => mapBackendKeyToStateKey(e, translationMap))
                    .join(', ')}
                </Typography>
              </>
              )}
              {aboutData.socialMedia
                  && aboutData.socialMedia.map((url) => (
                    <SocialIcon
                      key={url}
                      href={
                        url.indexOf('https://') || url.indexOf('http://')
                          ? url
                          : `https://${url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SocialMediaIcon url={url} />
                    </SocialIcon>
                  ))}

              {aboutData.calendly && (
              <CalendlyBox>
                <Typography align="center">Persönliches Beratungsgespräch buchen:</Typography>
                <Btn
                  buttonName="Termin finden"
                  href={aboutData.calendly}
                />
              </CalendlyBox>
              )}
            </TextBox>
          </PersonalDataBox>
          <DescriptionBox>
            <Typography variant="h2">Über mich</Typography>
            <DescriptionText>{aboutData.description}</DescriptionText>
            {aboutData.audio.id
                && aboutData.audio.id !== ''
                && (aboutData.audio.src.includes('mp3')
                  || aboutData.audio.src.includes('wav')) && (
                  <>
                    <Pbold17>Audio</Pbold17>
                    <Box>
                      <AudioPlayerDetail
                        sx={{ width: 200 }}
                        src={aboutData.audio.src}
                      />
                    </Box>
                  </>
            )}
          </DescriptionBox>

          <FocalPointsBox>
            {(aboutData.genrePrivate || aboutData.genreBusiness) && (
            <FocalBox>
              <Typography className="subheader">Genre</Typography>
              {aboutData.genrePrivate && (
              <Focus checked>Private Readings</Focus>
              )}
              {aboutData.genreBusiness && (
              <Focus checked>Business Readings</Focus>
              )}
            </FocalBox>
            )}
            {(aboutData.focusBusiness
                || aboutData.focusCareer
                || aboutData.focusFamily
                || aboutData.focusLove) && (
                <FocalBox>
                  <Typography className="subheader">HD-Spezialgebiete</Typography>
                  {aboutData.focusFamily && (
                    <Focus checked>Familie &#038; Kinder</Focus>
                  )}
                  {aboutData.focusLove && (
                    <Focus checked>Liebe &#038; Partnerschaft</Focus>
                  )}
                  {aboutData.focusBusiness && (
                    <Focus checked>Business</Focus>
                  )}
                  {aboutData.focusCareer && (
                    <Focus checked>Karriere</Focus>
                  )}
                </FocalBox>
            )}
            {(aboutData.focusCoaching
                || aboutData.focusDigital
                || aboutData.focusMediation) && (
                <FocalBox>
                  <Typography className="subheader">Schwerpunkte</Typography>
                  {aboutData.focusCoaching && (
                    <Focus checked>Coaching</Focus>
                  )}
                  {aboutData.focusDigital && (
                    <Focus checked>Digital</Focus>
                  )}
                  {aboutData.focusMediation && (
                    <Focus checked>Mediation</Focus>
                  )}
                  {aboutData.additionalFocus
                    && aboutData.additionalFocus.map((el, index) => (
                      <Focus key={index} checked>
                        {el.reader_profile_focus_hdfocus_additional_focus}
                      </Focus>
                    ))}
                </FocalBox>
            )}
          </FocalPointsBox>
        </>
      ) : (
        console.log('Es sind keine Daten über diese Person verfügbar.')
      )}
    </Wrapper>
  );
}

export default About;

function SocialMediaIcon({ url }) {
  if (url.indexOf('twitter') !== -1) return <Twitter />;
  if (url.indexOf('facebook') !== -1) return <Facebook />;
  if (url.indexOf('instagram') !== -1) return <Instagram />;
  if (url.indexOf('youtube') !== -1) return <YouTube />;
  if (url.indexOf('linkedin') !== -1) return <LinkedIn />;

  return <Web />;
}

// Styling

const Wrapper = styled(
  Container,
  {}
)({
  display: 'flex',
  flexDirection: 'column',

  [theme.breakpoints.up('laptop')]: {
    flexDirection: 'row',
    gap: 50,
  },
});

const PersonalDataBox = styled(
  Box,
  {}
)({
  margin: '0 auto',
  width: '100%',

  borderRadius: '10px 10px 0 0',
  background: theme.palette.backgroundgrey,

  textAlign: 'center',

  [theme.breakpoints.up('laptop')]: {
    width: '28%',
    minWidth: 350,
    maxWidth: 400,
  },
});

const DescriptionBox = styled(
  Box,
  {}
)({
  margin: '20px 0',
  width: '100%',

  '& h2': {
    margin: '20px 0',

    fontWeight: 700,
  },

  [theme.breakpoints.up('laptop')]: {
    margin: 0,
    width: '46%',

    '& h2': {
      margin: '10px 0 0',
    },
  },
});

const DescriptionText = styled(
  Typography,
  {}
)({
  ...theme.typography.pSmall,

  marginBottom: 60,

  whiteSpace: 'pre-wrap',

  [theme.breakpoints.up('laptop')]: {
    ...theme.typography.p16,

    marginBottom: '40px',
  },
});

const FocalBox = styled(
  Box,
  {}
)({
  margin: '20px 0',
  width: '100%',

  '& .subheader': {
    ...theme.typography.pBold17,
  },
});

const FocalPointsBox = styled(
  Box,
  {}
)({
  margin: '0 auto',
  width: '100%',
  maxWidth: 400,

  [theme.breakpoints.up('laptop')]: {
    width: '28%',
    maxWidth: 'unset',
  },

  '& .MuiTypography-h3': {
    marginTop: 20,
  },

  '& .MuiBox-root': {
    '&:first-of-type > .MuiTypography-h6': {
      marginTop: 0,
    },

    '& .MuiTypography-h6': {
      marginTop: 30,
    },
  },
});

const Image = styled(
  Avatar,
  {}
)({
  width: '100%',
  height: 'auto',

  borderRadius: '10px 10px 0 0',

  objectFit: 'contain',
  objectPosition: 'center center',
  aspectRatio: '1 / 1',
});

const TextBox = styled(
  Box,
  {}
)({
  padding: '28px 35px 25px',
});

const LocationBox = styled(
  Box,
  {}
)({
  display: 'flex',
  margin: '10px 0',

  justifyContent: 'center',
  alignItems: 'center',

  '& .MuiTypography-root': {
    marginBlockStart: 0,
    marginBlockEnd: 0,
  },
});

const Stats = styled(
  Box,
  {}
)({
  display: 'flex',
  margin: '10px 0',
  height: 31,

  gap: 3,
  justifyContent: 'center',
  alignItems: 'center',

  '& .MuiRating-root': {
    color: theme.palette.yellowOne,
  },
});

const Pbold = styled(
  Typography,
  {}
)({
  fontWeight: 700,
});

const Pbold17 = styled(
  Typography,
  {}
)({
  ...theme.typography.pBold17,
});

const SocialIcon = styled(
  Link,
  {}
)({
  marginRight: 10,
  width: 19,
  height: 19,

  opacity: 1,

  color: theme.palette.darkgrey,
  textDecoration: 'none',

  transition: 'opacity 250ms ease-in-out',

  '&:hover': {
    opacity: 0.7,

    transition: 'opacity 250ms ease-in-out',
  },
});

const CalendlyBox = styled(
  Stack,
  {}
)({
  marginTop: 30,
  gap: 10,

  textAlign: 'center',

  [theme.breakpoints.up('tablet')]: {
    alignItems: 'center',
    justifyContent: 'center',

    textAlign: 'left',
  },
});
